import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Calculator, Calendar, FileText, Star, ChevronRight, ShoppingBag, Play } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NutritionPage = () => {
  const [calculatorData, setCalculatorData] = useState({
    weight: '',
    height: '',
    age: '',
    gender: 'male',
    activity_level: 'moderate'
  });
  const [results, setResults] = useState(null);
  const [calculating, setCalculating] = useState(false);

  const handleCalculate = async (e) => {
    e.preventDefault();
    setCalculating(true);
    try {
      const response = await axios.post(`${API}/calculator/health`, {
        weight: parseFloat(calculatorData.weight),
        height: parseFloat(calculatorData.height),
        age: parseInt(calculatorData.age),
        gender: calculatorData.gender,
        activity_level: calculatorData.activity_level
      });
      setResults(response.data);
      toast.success('Calculation complete!');
    } catch (error) {
      toast.error('Please fill in all fields correctly');
    } finally {
      setCalculating(false);
    }
  };

  const services = [
    {
      title: 'Health Calculator',
      description: 'Calculate your BMI, BMR, and daily caloric needs based on your lifestyle.',
      icon: Calculator,
      action: 'scroll-calculator'
    },
    {
      title: 'Shop Products',
      description: 'Premium supplements, teas, healthy snacks, and meal prep accessories.',
      icon: ShoppingBag,
      link: '/nutrition/shop'
    },
    {
      title: 'Custom Meal Plans',
      description: 'Personalized nutrition plans tailored to your health goals and dietary preferences.',
      icon: FileText,
      link: '/nutrition/meal-plans'
    },
    {
      title: 'Expert Consultations',
      description: 'One-on-one sessions with certified nutritionists and dieticians.',
      icon: Calendar,
      link: '/nutrition/consultations'
    }
  ];

  const reviews = [
    { name: 'Sarah M.', rating: 5, comment: 'The personalized meal plan changed my life! Lost 15 pounds in 3 months.', service: 'Monthly Meal Plan' },
    { name: 'James K.', rating: 5, comment: 'Professional consultation and the calorie calculator is incredibly accurate.', service: 'Consultation' }
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-20">
      {/* Hero */}
      <section className="relative h-[70vh] flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/6728515/pexels-photo-6728515.jpeg"
            alt="ARTERYVAIL Nutrition"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/95 via-[#0A0A0A]/70 to-transparent" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl"
          >
            <p className="text-sm tracking-[0.3em] uppercase text-[#E5D3B3] mb-4">ARTERYVAIL Nutrition</p>
            <h1 className="font-serif text-5xl md:text-7xl text-[#F5F2EB] mb-6">
              Nourish Your Body, Transform Your Life
            </h1>
            <p className="text-lg text-[#A3988F] leading-relaxed mb-8">
              Expert dietary care and personalized nutrition plans designed for your unique health journey.
            </p>
            <a href="#calculator" className="btn-primary inline-flex items-center space-x-3" data-testid="nutrition-calculate-btn">
              <span>Calculate Your Needs</span>
              <ArrowRight className="w-4 h-4" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Services */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-sm tracking-[0.3em] uppercase text-[#E5D3B3] mb-4">Our Services</p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#F5F2EB]">Comprehensive Nutrition Care</h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="luxury-card rounded-xl p-8"
                style={{ borderColor: 'rgba(61, 35, 20, 0.3)' }}
              >
                <service.icon className="w-10 h-10 text-[#E5D3B3] mb-6" />
                <h3 className="font-serif text-2xl text-[#F5F2EB] mb-3">{service.title}</h3>
                <p className="text-[#A3988F] mb-6">{service.description}</p>
                {service.link ? (
                  <Link
                    to={service.link}
                    className="inline-flex items-center space-x-2 text-[#E5D3B3] hover:text-[#FAD6C6] transition-colors"
                    data-testid={`nutrition-service-${index}-link`}
                  >
                    <span className="text-sm tracking-wider uppercase">Learn More</span>
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                ) : (
                  <a
                    href="#calculator"
                    className="inline-flex items-center space-x-2 text-[#E5D3B3] hover:text-[#FAD6C6] transition-colors"
                    data-testid="nutrition-calculator-link"
                  >
                    <span className="text-sm tracking-wider uppercase">Try Now</span>
                    <ChevronRight className="w-4 h-4" />
                  </a>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Brand Video Section */}
      <section className="section-padding bg-[#3D2314]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-sm tracking-[0.3em] uppercase text-[#FFCBA4] mb-4">Our Story</p>
              <h2 className="font-serif text-4xl text-[#F5F2EB] mb-6">The ARTERYVAIL Nutrition Vision</h2>
              <p className="text-[#A3988F] leading-relaxed mb-6">
                At ARTERYVAIL Nutrition, we believe that true wellness begins with understanding your body's unique needs. 
                Our team of certified nutritionists and dieticians are dedicated to transforming lives through personalized 
                dietary guidance and premium nutrition products.
              </p>
              <p className="text-[#A3988F] leading-relaxed mb-8">
                From custom meal plans to expert consultations, we're here to support your journey to optimal health. 
                Watch our story to learn more about our mission and the experts behind ARTERYVAIL Nutrition.
              </p>
              <Link to="/nutrition/consultations" className="btn-primary inline-flex items-center space-x-3" data-testid="nutrition-meet-experts-btn">
                <span>Meet Our Experts</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="relative rounded-xl overflow-hidden aspect-video group cursor-pointer">
                <img
                  src="https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg"
                  alt="ARTERYVAIL Nutrition"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-[#0A0A0A]/40 flex items-center justify-center group-hover:bg-[#0A0A0A]/30 transition-colors">
                  <div className="w-20 h-20 bg-[#E5D3B3]/20 backdrop-blur-sm rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Play className="w-8 h-8 text-[#F5F2EB] ml-1" />
                  </div>
                </div>
                <div className="absolute bottom-4 left-4 right-4 text-center">
                  <p className="text-xs text-[#A3988F] bg-black/50 px-3 py-1 rounded-full inline-block">
                    Video Coming Soon
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <section id="calculator" className="section-padding bg-[#1A1514]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-sm tracking-[0.3em] uppercase text-[#E5D3B3] mb-4">Health Calculator</p>
              <h2 className="font-serif text-4xl text-[#F5F2EB] mb-6">Calculate Your Health Metrics</h2>
              <p className="text-[#A3988F] mb-8">
                Get instant insights into your BMI, BMR, and personalized caloric recommendations based on your lifestyle and goals.
              </p>

              <form onSubmit={handleCalculate} className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-[#A3988F] mb-2">Weight (kg)</label>
                    <input
                      type="number"
                      value={calculatorData.weight}
                      onChange={(e) => setCalculatorData({...calculatorData, weight: e.target.value})}
                      className="luxury-input"
                      placeholder="70"
                      required
                      data-testid="calculator-weight-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[#A3988F] mb-2">Height (cm)</label>
                    <input
                      type="number"
                      value={calculatorData.height}
                      onChange={(e) => setCalculatorData({...calculatorData, height: e.target.value})}
                      className="luxury-input"
                      placeholder="175"
                      required
                      data-testid="calculator-height-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-[#A3988F] mb-2">Age</label>
                    <input
                      type="number"
                      value={calculatorData.age}
                      onChange={(e) => setCalculatorData({...calculatorData, age: e.target.value})}
                      className="luxury-input"
                      placeholder="30"
                      required
                      data-testid="calculator-age-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-[#A3988F] mb-2">Gender</label>
                    <select
                      value={calculatorData.gender}
                      onChange={(e) => setCalculatorData({...calculatorData, gender: e.target.value})}
                      className="luxury-select"
                      data-testid="calculator-gender-select"
                    >
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-[#A3988F] mb-2">Activity Level</label>
                  <select
                    value={calculatorData.activity_level}
                    onChange={(e) => setCalculatorData({...calculatorData, activity_level: e.target.value})}
                    className="luxury-select"
                    data-testid="calculator-activity-select"
                  >
                    <option value="sedentary">Sedentary (little or no exercise)</option>
                    <option value="light">Light (exercise 1-3 days/week)</option>
                    <option value="moderate">Moderate (exercise 3-5 days/week)</option>
                    <option value="active">Active (exercise 6-7 days/week)</option>
                    <option value="very_active">Very Active (intense exercise daily)</option>
                  </select>
                </div>

                <button
                  type="submit"
                  disabled={calculating}
                  className="btn-primary w-full flex items-center justify-center space-x-2"
                  data-testid="calculator-submit-btn"
                >
                  {calculating ? (
                    <div className="loader w-5 h-5" />
                  ) : (
                    <>
                      <Calculator className="w-4 h-4" />
                      <span>Calculate</span>
                    </>
                  )}
                </button>
              </form>
            </motion.div>

            {/* Results */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="flex items-center"
            >
              {results ? (
                <div className="w-full space-y-6">
                  <div className="luxury-card rounded-xl p-8">
                    <h3 className="font-serif text-2xl text-[#F5F2EB] mb-6">Your Results</h3>
                    
                    <div className="space-y-6">
                      <div className="flex justify-between items-center pb-4 border-b border-[#E5D3B3]/10">
                        <span className="text-[#A3988F]">BMI</span>
                        <div className="text-right">
                          <span className="text-2xl font-semibold text-[#E5D3B3]" data-testid="result-bmi">{results.bmi}</span>
                          <p className="text-sm text-[#A3988F]">{results.bmi_category}</p>
                        </div>
                      </div>

                      <div className="flex justify-between items-center pb-4 border-b border-[#E5D3B3]/10">
                        <span className="text-[#A3988F]">BMR (Basal Metabolic Rate)</span>
                        <span className="text-2xl font-semibold text-[#E5D3B3]" data-testid="result-bmr">{results.bmr} cal</span>
                      </div>

                      <div className="flex justify-between items-center pb-4 border-b border-[#E5D3B3]/10">
                        <span className="text-[#A3988F]">TDEE (Daily Energy)</span>
                        <span className="text-2xl font-semibold text-[#E5D3B3]" data-testid="result-tdee">{results.tdee} cal</span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="luxury-card rounded-xl p-6 text-center">
                      <p className="text-xs tracking-wider uppercase text-[#A3988F] mb-2">To Lose</p>
                      <p className="text-xl font-semibold text-[#FAD6C6]" data-testid="result-lose">{results.calories_lose}</p>
                      <p className="text-xs text-[#A3988F]">cal/day</p>
                    </div>
                    <div className="luxury-card rounded-xl p-6 text-center">
                      <p className="text-xs tracking-wider uppercase text-[#A3988F] mb-2">Maintain</p>
                      <p className="text-xl font-semibold text-[#E5D3B3]" data-testid="result-maintain">{results.calories_maintain}</p>
                      <p className="text-xs text-[#A3988F]">cal/day</p>
                    </div>
                    <div className="luxury-card rounded-xl p-6 text-center">
                      <p className="text-xs tracking-wider uppercase text-[#A3988F] mb-2">To Gain</p>
                      <p className="text-xl font-semibold text-[#8C7B6B]" data-testid="result-gain">{results.calories_gain}</p>
                      <p className="text-xs text-[#A3988F]">cal/day</p>
                    </div>
                  </div>

                  <Link
                    to="/nutrition/meal-plans"
                    className="btn-secondary w-full flex items-center justify-center space-x-2"
                    data-testid="result-meal-plan-link"
                  >
                    <span>Get Custom Meal Plan</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              ) : (
                <div className="w-full luxury-card rounded-xl p-12 text-center">
                  <Calculator className="w-16 h-16 text-[#E5D3B3]/30 mx-auto mb-6" />
                  <h3 className="font-serif text-2xl text-[#F5F2EB] mb-3">Your Results Will Appear Here</h3>
                  <p className="text-[#A3988F]">Fill in your details and calculate to see personalized health metrics.</p>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-sm tracking-[0.3em] uppercase text-[#E5D3B3] mb-4">Testimonials</p>
            <h2 className="font-serif text-4xl text-[#F5F2EB]">Success Stories</h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {reviews.map((review, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="luxury-card rounded-xl p-8"
              >
                <div className="flex space-x-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-5 h-5 ${i < review.rating ? 'star-filled fill-current' : 'star-empty'}`}
                    />
                  ))}
                </div>
                <p className="text-[#F5F2EB] text-lg mb-6">"{review.comment}"</p>
                <div className="flex justify-between items-center">
                  <span className="text-[#E5D3B3] font-semibold">{review.name}</span>
                  <span className="text-sm text-[#A3988F]">{review.service}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-[#1A1514]">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-serif text-4xl text-[#F5F2EB] mb-6">Ready to Start Your Health Journey?</h2>
            <p className="text-[#A3988F] mb-8">Book a consultation with our expert nutritionists today.</p>
            <Link to="/nutrition/consultations" className="btn-primary inline-flex items-center space-x-3" data-testid="nutrition-book-consultation-btn">
              <span>Book Consultation</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default NutritionPage;
